<template>
  <div class="api-documentation">
    <h1>Documentação da API de Consulta de Produtos - Aldo Solar</h1>

    <!-- Navegação -->
    <nav class="documentation-nav">
      <ul>
        <li v-for="(section, index) in sections" :key="index">
          <a :href="'#' + section.id" @click.prevent="scrollToSection(section.id)">
            {{ section.title }}
          </a>
        </li>
      </ul>
    </nav>

    <!-- Conteúdo Principal -->
    <div class="documentation-content">
      <!-- Visão Geral -->
      <section id="visao-geral" class="section">
        <h2>Visão Geral</h2>
        <p>Esta API permite a consulta de produtos e insumos da Aldo Solar através de endpoints específicos. Para acessar os dados, é necessário realizar autenticação através de um token com validade de 1 hora.</p>
      </section>

      <!-- Autenticação -->
      <section id="autenticacao" class="section">
        <h2>Autenticação</h2>
        <h3>Geração de Token</h3>
        <p>Para obter o token de acesso, é necessário seguir os seguintes passos:</p>
        <ol>
          <li>Obter as credenciais de acesso (Client ID e Client Key) junto à Aldo Solar</li>
          <li>Gerar uma assinatura (signature) utilizando as credenciais</li>
          <li>Solicitar o token utilizando a assinatura gerada</li>
        </ol>

        <h4>Geração da Assinatura</h4>
        <p>A assinatura deve ser gerada utilizando o algoritmo HMAC-SHA256. Exemplo de implementação em JavaScript:</p>
        <pre><code>{{ signatureCode }}</code></pre>

        <h4>Solicitação do Token</h4>
        <pre><code>{{ tokenRequest }}</code></pre>
      </section>

      <!-- Endpoints -->
      <section id="endpoints" class="section">
        <h2>Endpoints</h2>
        <div class="endpoint">
          <h3>1. Lista de Produtos</h3>
          <pre><code>{{ endpoints.products }}</code></pre>
        </div>
        <div class="endpoint">
          <h3>2. Insumos dos Produtos</h3>
          <pre><code>{{ endpoints.inputs }}</code></pre>
        </div>
      </section>

      <!-- Filtros e Parâmetros -->
      <section id="filtros-parametros" class="section">
        <h2>Filtros e Parâmetros</h2>
        <h3>Parâmetros de Consulta</h3>
        <table class="parameters-table">
          <thead>
            <tr>
              <th>Parâmetro</th>
              <th>Tipo</th>
              <th>Descrição</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="param in parameters" :key="param.name">
              <td>{{ param.name }}</td>
              <td>{{ param.type }}</td>
              <td>{{ param.description }}</td>
            </tr>
          </tbody>
        </table>

        <h3>Opções de Ordenação (orderby)</h3>
        <ul>
          <li v-for="(option, value) in orderOptions" :key="value">
            {{ value }}: {{ option }}
          </li>
        </ul>
      </section>

      <!-- Exemplos de Uso -->
      <section id="exemplos-uso" class="section">
        <h2>Exemplos de Uso</h2>
        <div v-for="(example, index) in examples" :key="index" class="example">
          <h3>Exemplo {{ index + 1 }}: {{ example.title }}</h3>
          <pre><code>{{ example.code }}</code></pre>
        </div>
      </section>

      <!-- Cadastro de Clientes -->
      <section id="cadastro-clientes" class="section">
        <h2>Cadastro de Clientes</h2>
        <h3>Endpoint de Cadastro</h3>
        <pre><code>{{ clientEndpoint }}</code></pre>

        <h3>Exemplo de Payload</h3>
        <pre><code>{{ clientPayload }}</code></pre>

        <h3>Observações Importantes</h3>
        <ol>
          <li v-for="(note, index) in importantNotes" :key="index">
            {{ note }}
          </li>
        </ol>
      </section>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ApiDocumentation',
  data() {
    return {
      sections: [
        { id: 'visao-geral', title: 'Visão Geral' },
        { id: 'autenticacao', title: 'Autenticação' },
        { id: 'endpoints', title: 'Endpoints' },
        { id: 'filtros-parametros', title: 'Filtros e Parâmetros' },
        { id: 'exemplos-uso', title: 'Exemplos de Uso' },
        { id: 'cadastro-clientes', title: 'Cadastro de Clientes' }
      ],
      signatureCode: `async function gerarAssinatura(clientId, clientKey) {
     const encoder = new TextEncoder();
     const keyData = encoder.encode(clientKey);
     const data = encoder.encode(clientId);

     const key = await crypto.subtle.importKey(
         'raw',
         keyData,
         { name: 'HMAC', hash: 'SHA-256' },
         false,
         ['sign']
     );

     const signature = await crypto.subtle.sign('HMAC', key, data);
     return btoa(String.fromCharCode(...new Uint8Array(signature)));
 }`,
      tokenRequest: `POST https://www.aldo.com.br/wcf/Login.svc/v1/get-token
 Content-Type: application/json

 {
     "clientId": "SEU_CLIENT_ID",
     "signature": "SUA_ASSINATURA"
 }`,
      endpoints: {
        products: `GET https://www.aldo.com.br/wcf/v1/get-products
 Content-Type: application/json
 Authorization: {{TOKEN}}
 ClientId: {{CLIENT_ID}}`,
        inputs: `GET https://www.aldo.com.br/wcf/v1/get-products-inputs
 Content-Type: application/json
 Authorization: {{TOKEN}}
 ClientId: {{CLIENT_ID}}`
      },
      parameters: [
        { name: 'slug', type: 'string', description: 'Identificador da categoria do produto' },
        { name: 'origem', type: 'string', description: 'Origem da consulta (ex: "categoria")' },
        { name: 'filtroAtributos', type: 'string', description: 'IDs dos filtros separados por ";"' },
        { name: 'offset', type: 'number', description: 'Paginação (0 = primeiros 30 produtos)' },
        { name: 'filterId', type: 'number', description: 'ID do filtro' },
        { name: 'orderby', type: 'number', description: 'Ordenação dos resultados' }
      ],
      orderOptions: {
        '-1': 'Maior preço',
        '1': 'Menor preço',
        '2': 'Mais Vendidos',
        '3': 'Menor Potência',
        '4': 'Maior Potência'
      },
      examples: [
        {
          title: 'Consulta Básica',
          code: `{
     "slug": "energia-solar/gerador-de-energia-solar-fotovoltaico",
     "origem": "categoria",
     "filtroAtributos": null,
     "offset": 0,
     "filterId": 0,
     "orderby": 1
 }`
        },
        {
          title: 'Consulta com Filtros Específicos',
          code: `{
     "slug": "energia-solar/gerador-de-energia-solar-fotovoltaico",
     "origem": "categoria",
     "filtroAtributos": "596,141",
     "offset": 0,
     "filterId": 0,
     "orderby": 1
 }`
        },
        {
          title: 'Consulta com Filtro de Potência',
          code: `{
     "slug": "energia-solar/gerador-de-energia-solar-fotovoltaico",
     "origem": "categoria",
     "filtroAtributos": "245;3:10~15",
     "offset": 0,
     "filterId": 0,
     "orderby": 1
 }`
        }
      ],
      clientEndpoint: `POST https://www.aldo.com.br/wcf/ClienteVendaDireta.svc/v1/insert-pf
 Content-Type: application/json
 Authorization: {{TOKEN}}
 ClientId: {{CLIENT_ID}}`,
      clientPayload: `{
     "Nome": "Nome do Cliente",
     "Cpf": "123.456.789-00",
     "Sexo": 0,
     "Endereco": "Rua Exemplo",
     "Bairro": "Bairro Exemplo",
     "Cidade": "Cidade Exemplo",
     "Uf": "SP",
     "Numero": 123,
     "Cep": "12345-678",
     "Telefone": "11999999999",
     "Email": "email@exemplo.com.br"
 }`,
      importantNotes: [
        'O token gerado tem validade de 1 hora',
        'A paginação é feita em lotes de 30 produtos',
        'Os slugs das categorias podem ser encontrados no arquivo menus.json',
        'Os filtros disponíveis podem ser consultados no arquivo filtros.json',
        'A potência deve ser sempre o último filtro na string de filtros',
        'No cadastro de clientes, o campo "Sexo" deve ser 0 para masculino, 1 para feminino e 2 para outros',
        'Todos os campos são obrigatórios no payload de cadastro de clientes, com exceção do campos campos "Endereco", "Bairro", "Cidade", "Uf" e "Numero"',
        'Caso não tenha o número do endereço, não é necessário preencher o campo "Numero" ou ele pode ser "null"',
        'Os campos "Endereco", "Bairro", "Cidade" e "Uf" são opcionais e serão preenchidos através do CEP informado. Porém se o CEP for inválido, esses campos se tornarão obrigatórios',
      ]
    }
  },
  methods: {
    scrollToSection(sectionId) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }
}
</script>

<style scoped>
.api-documentation {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  font-family: Arial, sans-serif;
}

.documentation-nav {
  position: sticky;
  top: 0;
  background: white;
  padding: 15px;
  border-bottom: 1px solid #eee;
  margin-bottom: 20px;
}

.documentation-nav ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  gap: 20px;
}

.documentation-nav a {
  text-decoration: none;
  color: #2c3e50;
  font-weight: 500;
}

.documentation-nav a:hover {
  color: #42b983;
}

.section {
  margin-bottom: 40px;
  padding: 20px;
  border-radius: 8px;
  background: #f8f9fa;
}

h1 {
  color: #2c3e50;
  margin-bottom: 30px;
}

h2 {
  color: #337ab7;
  margin-bottom: 20px;
}

h3 {
  color: #2c3e50;
  margin: 15px 0;
}

pre {
  background: #2c3e50;
  color: #fff;
  padding: 15px;
  border-radius: 4px;
  overflow-x: auto;
}

code {
  font-family: 'Courier New', Courier, monospace;
  box-shadow: none;
  font-size: 15px;
}

.parameters-table {
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
}

.parameters-table th,
.parameters-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

.parameters-table th {
  background-color: #f8f9fa;
  font-weight: bold;
}

.endpoint {
  margin-bottom: 30px;
}

.example {
  margin-bottom: 30px;
}

ol, ul {
  padding-left: 20px;
}

li {
  margin-bottom: 10px;
}
</style>
